import { BusinessError } from '@marketing-milk/interfaces'

export type Provider = 'meta' | 'google'
export type Severity = 'critical' | 'warning'

export interface BusinessErrorInfo {
  provider: Provider
  severity: Severity
  title: string
  summary: string
  steps: string[]
  learnMoreUrl?: string
}

// critical = actively blocks ad builder from working (matches the same split used in the FB Builder
// app's error banner); warning = a real problem but doesn't block campaign delivery. Shared by
// ErrorCenter and the Facebook Builder panel's per-field health display so both classify errors the
// same way. Steps differ by whether the business is on Facebook Builder (system user) or the legacy
// Authorizer (per-user OAuth) flow — the two need genuinely different remediation instructions, so this
// is a function of both rather than a flat lookup.
export const getBusinessErrorInfo = (
  error: BusinessError,
  isBuilder: boolean
): BusinessErrorInfo => {
  // Builder's fix is always "click Reconnect on the field in the Facebook Builder panel" — the general
  // system-user/Reconnect explanation lives once in ErrorCenter's header (shown whenever isBuilder),
  // not repeated per card here — just the concrete action for this specific field/permission.
  const builderReconnectSteps = (assetLabel: string, taskLabel: string) => [
    `In the Facebook Builder under Settings -> Social Media, click Reconnect on the ${assetLabel} field to have the system user re-request ${taskLabel}.`,
  ]

  // If the client shared the asset into our partner Business Manager (rather than adding a person
  // directly), what matters is the permission granted TO that Business Manager, not any individual's
  // personal access — this is easy to mix up, so spell out exactly where to go check it.
  const builderSharedAssetSteps = (assetLabel: string, taskLabel: string) => [
    `If the client shared this ${assetLabel} with our Business Manager instead of adding you personally, Reconnect depends on what permission OUR Business Manager was granted — not your own personal access.`,
    `To check: in OUR Business Manager, go to Users -> Partners.`,
    `Search for the client's Business Manager.`,
    `Open Assets assigned to you, and find the ${assetLabel}.`,
    `View its assignment and confirm it includes ${taskLabel} — that's what's actually needed here.`,
    `The client may need to confirm or re-grant that on their end.`,
  ]

  // Same shared-Business-Manager scenario as Builder's, but the fix is re-adding the Authorizer as a
  // connected user (or confirming the shared grant) rather than clicking Reconnect.
  const authorizerSharedAssetSteps = (assetLabel: string, taskLabel: string) => [
    `If the client shared this ${assetLabel} with our Business Manager instead of adding the Authorizer personally, what matters is what permission OUR Business Manager was granted — not the Authorizer's own personal access.`,
    `To check: in OUR Business Manager, go to Users -> Partners.`,
    `Search for the client's Business Manager.`,
    `Open Assets assigned to you, and find the ${assetLabel}.`,
    `View its assignment and confirm it includes ${taskLabel} — that's what's actually needed here.`,
    `The client may need to confirm or re-grant that on their end.`,
  ]

  switch (error) {
    case BusinessError.ad_account_access_error:
      return {
        provider: 'meta',
        severity: 'critical',
        title: 'Ad Account Access Error',
        summary: isBuilder
          ? 'Facebook rejected our requests for this ad account. The system user is missing required permissions, or there is an issue with the Ad Account.'
          : 'Facebook rejected our requests for this ad account. The Ad Authorizer is missing permissions, has an expired token, or there is an issue with the Ad Account.',
        steps: isBuilder
          ? [
              ...builderReconnectSteps(
                'Ad Account',
                'Manage campaigns (ads) and View performance access'
              ),
              ...builderSharedAssetSteps(
                'Ad Account',
                'Manage campaigns (ads) and View performance'
              ),
              "If Reconnect still fails, check the ad account itself on Facebook's account settings screen for account-level restrictions.",
            ]
          : [
              "Check that the Ad Authorizer is added to the Facebook Ad Account as a connected user with Manage campaigns (ads) and View performance access on Facebook's Business Manager.",
              ...authorizerSharedAssetSteps(
                'Ad Account',
                'Manage campaigns (ads) and View performance'
              ),
              'Update the Authorizer on Marketing Milk if a different user should own this connection.',
              "Check the authorizer's Facebook connection status under User Preferences — if it shows as expired or invalid, have them re-log in to refresh it.",
              "If the issue persists, check the ad account itself on Facebook's account settings screen for account-level restrictions.",
              'Once resolved, you should be able to clear the error.',
            ],
      }
    case BusinessError.page_access_error:
      return {
        provider: 'meta',
        severity: 'critical',
        title: 'Page Access Error',
        summary: isBuilder
          ? 'The system user is missing required permissions on this Page.'
          : 'The Page Authorizer is missing permissions or has an expired token.',
        steps: isBuilder
          ? [
              ...builderReconnectSteps('Page', 'Ads and Insights access'),
              ...builderSharedAssetSteps('Page', 'Ads and Insights'),
            ]
          : [
              "Ensure the Page Authorizer is added to the Facebook Page as a connected user with Ads and Insights access on Facebook's Business Manager.",
              ...authorizerSharedAssetSteps('Page', 'Ads and Insights'),
              'Update the Authorizer on Marketing Milk if a different user should own this connection.',
              "Check the authorizer's Facebook connection status under User Preferences — if it shows as expired or invalid, have them re-log in to refresh it.",
              'Once resolved, you should be able to clear the error.',
            ],
      }
    case BusinessError.lead_access_error:
      return {
        provider: 'meta',
        severity: 'warning',
        title: 'Lead Access Error',
        summary: isBuilder
          ? 'The system user is missing Lead access on this Page.'
          : 'The Page Authorizer is missing Lead access or has an expired token.',
        steps: isBuilder
          ? [
              'This usually happens one of two ways: custom Leads access on the Page was removed or reset in Facebook Business Settings after the system user was originally granted it, or the Page itself lost its relationship to whichever Business Manager the system user operates under.',
              ...builderReconnectSteps('Page', 'Lead access'),
              ...builderSharedAssetSteps('Page', 'Leads'),
            ]
          : [
              'This usually happens one of two ways: custom Leads access on the Page was removed or reset in Facebook Business Settings, or the shared partner Business Manager lost its Lead access relationship to the client.',
              "Check the Page in Facebook Business Settings to see whether Leads access is still assigned there — if it's been reset or removed, re-add the Page Authorizer as a connected user with Leads access.",
              ...authorizerSharedAssetSteps('Page', 'Leads'),
              'Update the Authorizer on Marketing Milk if a different user should own this connection.',
              "Check the authorizer's Facebook connection status under User Preferences — if it shows as expired or invalid, have them re-log in to refresh it.",
              'Once resolved, you should be able to clear the error.',
            ],
      }
    case BusinessError.offline_upload_error:
      return {
        provider: 'meta',
        severity: 'warning',
        title: 'Offline Upload Error',
        summary: 'Uploads to Facebook have failed due to improper configuration.',
        steps: isBuilder
          ? [
              'Make sure this business has a Dataset ID configured with the Conversions API, not an Offline Event Set ID.',
              ...builderReconnectSteps('Dataset', 'access to it'),
              'If you recently switched Ad Accounts, the Dataset may no longer be connected to the currently selected Ad Account — check the connection in Events Manager, or reselect the Dataset in the Facebook Builder panel above.',
            ]
          : [
              'Make sure this business has a Dataset ID configured with the Conversions API.',
              'If it is still using an Offline Event Set ID, uploads to Facebook will continue to fail — migrate it to a Dataset ID.',
            ],
      }
    case BusinessError.google_ads_access_error:
      return {
        provider: 'google',
        severity: 'warning',
        title: 'Google Ads Access Error',
        summary: 'Google rejected our requests for this Google Ads account.',
        steps: [
          "The authorizer's Google account is no longer connected.",
          'Have the authorizer reconnect their Google account under User Preferences.',
          'Or, update the Google Authorizer on Marketing Milk to a user who has a connected Google account.',
          'Once resolved, you should be able to clear the error.',
        ],
      }
  }
}

export const severityColors: Record<
  Severity,
  { border: string; background: string; text: string }
> = {
  critical: { border: '#dc3545', background: '#fdf2f3', text: '#7a1620' },
  warning: { border: '#ffc107', background: '#fffaf0', text: '#7a5b00' },
}
